import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  VStack,
  useDisclosure
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  FiArrowLeft,
  FiCheck,
  FiChevronDown,
  FiCopy,
  FiInfo,
  FiLink,
  FiLoader,
  FiPlus,
  FiSearch,
  FiX,
  FiGlobe,
  FiZap,
  FiAlertCircle
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { mockApi, type PreconnectEntry } from "../api/mockApi";

interface FormData {
  url: string;
  type: "dns-prefetch" | "preconnect";
  description: string;
  paths: string;
}

// Available paths for the dropdown
const availablePaths = [
  { value: "/", label: "Homepage", description: "Main landing page" },
  { value: "/buyer/home", label: "Buyer Home", description: "Buyer dashboard" },
  { value: "/buyer/search", label: "Property Search", description: "Search properties" },
  { value: "/buyer/favorites", label: "Saved Properties", description: "Favorite listings" },
  { value: "/seller/home", label: "Seller Home", description: "Seller dashboard" },
  { value: "/seller/valuation", label: "Home Valuation", description: "Property valuation tool" },
  { value: "/seller/guide", label: "Selling Guide", description: "How to sell guide" },
  { value: "/agent/dashboard", label: "Agent Dashboard", description: "Agent portal" },
  { value: "/agent/listings", label: "Manage Listings", description: "Agent listings" },
  { value: "/resources", label: "Resources", description: "Resource center" },
  { value: "/blog", label: "Blog", description: "Blog posts" },
  { value: "/about", label: "About Us", description: "Company information" },
  { value: "/contact", label: "Contact", description: "Contact form" }
];

export default function CreatePreconnect() {
  const [formData, setFormData] = useState<FormData>({
    url: "",
    type: "preconnect",
    description: "",
    paths: ""
  });

  const [urlError, setUrlError] = useState<string | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [pathDropdown, setPathDropdown] = useState<{ isOpen: boolean; searchQuery: string }>({
    isOpen: false,
    searchQuery: ""
  });
  const [isGlobal, setIsGlobal] = useState(true);
  const [createdEntry, setCreatedEntry] = useState<PreconnectEntry | null>(null);
  const { open, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Validate URL
  const validateUrl = (url: string): boolean => {
    if (!url) {
      setUrlError("URL is required");
      return false;
    }

    try {
      const urlObj = new URL(url);
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        setUrlError("URL must start with http:// or https://");
        return false;
      }
      setUrlError(null);
      return true;
    } catch {
      setUrlError("Invalid URL format");
      return false;
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<PreconnectEntry>) => {
      return mockApi.createPreconnectEntry(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["preconnectEntries"] });
      setCreatedEntry(data);
      onOpen();
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateUrl(formData.url)) {
      return;
    }

    const preconnectData = {
      url: formData.url,
      type: formData.type,
      description: formData.description || undefined,
      paths: !isGlobal && selectedPaths.length > 0 ? selectedPaths : undefined,
      status: "active" as const
    };

    createMutation.mutate(preconnectData);
  };

  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === "url" && value) {
      validateUrl(value);
    }
  };

  const handleGoToWebsite = () => {
    navigate("/website");
  };

  const copyToClipboard = () => {
    if (createdEntry) {
      navigator.clipboard.writeText(createdEntry.url);
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="6xl" py={8}>
        {/* Header */}
        <VStack gap={6} align="stretch" mb={8}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <Link to="/website">
                <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                  <FiArrowLeft />
                  Back to Website
                </Button>
              </Link>
              <Text color="gray.500">/</Text>
              <Text color="gray.700" fontWeight="medium">
                Create Preconnect Entry
              </Text>
            </HStack>
            <Badge colorScheme="green" px={3} py={1} rounded="full">
              <HStack gap={1}>
                <Icon as={FiPlus} />
                <Text>New Entry</Text>
              </HStack>
            </Badge>
          </HStack>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
          {/* Main Form */}
          <VStack as="form" onSubmit={handleSubmit} gap={6} align="stretch">
            {/* URL Configuration */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiLink} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  URL Configuration
                </Text>
                <Badge colorScheme="red" size="sm">
                  Required
                </Badge>
              </HStack>

              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Resource URL
                  </Text>
                  <Input
                    value={formData.url}
                    onChange={(e) => handleFieldChange("url", e.target.value)}
                    placeholder="https://cdn.example.com"
                    disabled={createMutation.isPending}
                    borderColor={urlError ? "red.500" : undefined}
                    _focus={{
                      borderColor: urlError ? "red.500" : "blue.500",
                      boxShadow: urlError ? "0 0 0 1px var(--chakra-colors-red-500)" : undefined
                    }}
                  />
                  {urlError && (
                    <HStack gap={1} mt={1}>
                      <Icon as={FiAlertCircle} color="red.500" boxSize={3} />
                      <Text fontSize="xs" color="red.500">
                        {urlError}
                      </Text>
                    </HStack>
                  )}
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Enter the full URL of the resource to preconnect
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Connection Type
                  </Text>
                  <HStack gap={2}>
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.type === "dns-prefetch" ? "solid" : "outline"}
                      colorScheme={formData.type === "dns-prefetch" ? "blue" : "gray"}
                      onClick={() => handleFieldChange("type", "dns-prefetch")}
                      disabled={createMutation.isPending}
                      flex={1}
                    >
                      DNS Prefetch
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.type === "preconnect" ? "solid" : "outline"}
                      colorScheme={formData.type === "preconnect" ? "blue" : "gray"}
                      onClick={() => handleFieldChange("type", "preconnect")}
                      disabled={createMutation.isPending}
                      flex={1}
                    >
                      Preconnect
                    </Button>
                  </HStack>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    DNS Prefetch only resolves DNS. Preconnect also establishes TCP/TLS connection.
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* Target Paths */}
            <Box bg="white" shadow="sm" rounded="lg" overflow="visible">
              <Box
                px={6}
                py={4}
                bg="gradient.to-r"
                bgGradient="linear(to-r, green.50, blue.50)"
                borderBottom="1px"
                borderColor="gray.200"
              >
                <HStack gap={2}>
                  <Icon as={FiGlobe} boxSize={5} color="green.600" />
                  <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                    Target Paths
                  </Text>
                </HStack>
              </Box>

              <Box p={6}>
                <VStack align="stretch" gap={4}>
                  {/* Global Toggle */}
                  <Box
                    as="button"
                    p={4}
                    bg={isGlobal ? "blue.50" : "gray.50"}
                    rounded="lg"
                    border="1px"
                    borderColor={isGlobal ? "blue.200" : "gray.200"}
                    cursor="pointer"
                    transition="all 0.2s"
                    _hover={{ borderColor: isGlobal ? "blue.300" : "gray.300" }}
                    onClick={() => {
                      setIsGlobal(!isGlobal);
                      if (!isGlobal) {
                        setSelectedPaths([]);
                      }
                    }}
                    w="full"
                  >
                    <HStack justify="space-between">
                      <VStack align="start" gap={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          Apply to All Pages
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Enable this to apply the preconnect globally across all paths
                        </Text>
                      </VStack>
                      <Box
                        position="relative"
                        w="50px"
                        h="26px"
                        bg={isGlobal ? "blue.500" : "gray.300"}
                        rounded="full"
                        transition="all 0.2s"
                      >
                        <Box
                          position="absolute"
                          top="2px"
                          left={isGlobal ? "26px" : "2px"}
                          w="22px"
                          h="22px"
                          bg="white"
                          rounded="full"
                          shadow="sm"
                          transition="all 0.2s"
                        />
                      </Box>
                    </HStack>
                  </Box>

                  {!isGlobal && (
                    <>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          Select Specific Paths
                        </Text>
                        <Box position="relative">
                          <Box
                            as="button"
                            w="full"
                            p={3}
                            border="1px"
                            borderColor="gray.300"
                            rounded="lg"
                            bg="white"
                            textAlign="left"
                            onClick={() => {
                              setPathDropdown(prev => ({
                                isOpen: !prev.isOpen,
                                searchQuery: ""
                              }));
                            }}
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{
                              borderColor: "purple.500",
                              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                              outline: "none"
                            }}
                          >
                            <HStack justify="space-between">
                              <Text>
                                {selectedPaths.length === 0 
                                  ? "Select paths..."
                                  : selectedPaths.length === availablePaths.length
                                  ? "All paths selected"
                                  : `${selectedPaths.length} path${selectedPaths.length !== 1 ? "s" : ""} selected`
                                }
                              </Text>
                              <Icon 
                                as={FiChevronDown} 
                                transform={pathDropdown.isOpen ? "rotate(180deg)" : "none"}
                                transition="transform 0.2s"
                              />
                            </HStack>
                          </Box>

                          {/* Dropdown Modal */}
                          {pathDropdown.isOpen && (
                            <>
                              {/* Backdrop */}
                              <Box
                                position="fixed"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                bg="blackAlpha.400"
                                zIndex={1400}
                                onClick={() => setPathDropdown({ isOpen: false, searchQuery: "" })}
                              />
                              
                              {/* Modal Content */}
                              <Box
                                position="fixed"
                                bg="white"
                                border="1px"
                                borderColor="gray.200"
                                rounded="lg"
                                shadow="2xl"
                                zIndex={1500}
                                w="600px"
                                maxW="90vw"
                                display="flex"
                                flexDirection="column"
                                left="50%"
                                top="50%"
                                transform="translate(-50%, -50%)"
                                maxHeight="80vh"
                              >
                                {/* Header with Search */}
                                <Box p={4} borderBottom="1px solid" borderColor="gray.200">
                                  <HStack justify="space-between" mb={3}>
                                    <Text fontWeight="semibold" fontSize="lg">Select Target Paths</Text>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setPathDropdown({ isOpen: false, searchQuery: "" })}
                                      aria-label="Close"
                                    >
                                      <Icon as={FiX} />
                                    </Button>
                                  </HStack>
                                  <HStack>
                                    <Icon as={FiSearch} color="gray.400" />
                                    <Input
                                      placeholder="Search paths..."
                                      value={pathDropdown.searchQuery}
                                      onChange={(e) =>
                                        setPathDropdown(prev => ({
                                          ...prev,
                                          searchQuery: e.target.value
                                        }))
                                      }
                                      size="md"
                                      border="1px"
                                      borderColor="gray.300"
                                      _focus={{
                                        borderColor: "purple.500",
                                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </HStack>
                                </Box>

                                {/* Select/Clear All Actions */}
                                {!pathDropdown.searchQuery && (
                                  <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                                    <HStack justify="space-between">
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="blue"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedPaths(
                                            availablePaths.map(p => p.value)
                                          );
                                        }}
                                      >
                                        Select All ({availablePaths.length})
                                      </Button>
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedPaths([]);
                                        }}
                                      >
                                        Clear All
                                      </Button>
                                    </HStack>
                                  </Box>
                                )}

                                {/* Path List - Scrollable */}
                                <Box flex="1" overflowY="auto" maxH="300px">
                                  {(() => {
                                    const filteredPaths = availablePaths.filter(path => {
                                      const searchQuery = pathDropdown.searchQuery.toLowerCase();
                                      return (
                                        path.value.toLowerCase().includes(searchQuery) ||
                                        path.label.toLowerCase().includes(searchQuery) ||
                                        (path.description && path.description.toLowerCase().includes(searchQuery))
                                      );
                                    });

                                    return filteredPaths.length > 0 ? (
                                      filteredPaths.map(path => {
                                        const isSelected = selectedPaths.includes(path.value);
                                        return (
                                          <Box
                                            key={path.value}
                                            as="button"
                                            w="full"
                                            p={3}
                                            textAlign="left"
                                            _hover={{ bg: "gray.50" }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isSelected) {
                                                setSelectedPaths(prev => prev.filter(p => p !== path.value));
                                              } else {
                                                setSelectedPaths(prev => [...prev, path.value]);
                                              }
                                            }}
                                            borderBottom="1px solid"
                                            borderColor="gray.100"
                                          >
                                            <HStack justify="space-between">
                                              <HStack gap={3}>
                                                <Box
                                                  w={4}
                                                  h={4}
                                                  border="2px"
                                                  borderColor={isSelected ? "purple.500" : "gray.300"}
                                                  rounded="sm"
                                                  bg={isSelected ? "purple.500" : "white"}
                                                  display="flex"
                                                  alignItems="center"
                                                  justifyContent="center"
                                                >
                                                  {isSelected && (
                                                    <Icon as={FiCheck} boxSize={2.5} color="white" />
                                                  )}
                                                </Box>
                                                <VStack align="start" gap={0}>
                                                  <Text fontSize="sm" fontWeight="medium">
                                                    {path.value}
                                                  </Text>
                                                  {path.description && (
                                                    <Text fontSize="xs" color="gray.500">
                                                      {path.description}
                                                    </Text>
                                                  )}
                                                </VStack>
                                              </HStack>
                                            </HStack>
                                          </Box>
                                        );
                                      })
                                    ) : (
                                      <Box p={8} textAlign="center">
                                        <Text fontSize="sm" color="gray.500">
                                          No paths found matching "{pathDropdown.searchQuery}"
                                        </Text>
                                      </Box>
                                    );
                                  })()}
                                </Box>

                                {/* Footer with count */}
                                <Box p={3} borderTop="1px solid" borderColor="gray.200" bg="gray.50">
                                  <Text fontSize="xs" color="gray.600" textAlign="center">
                                    {selectedPaths.length} of {availablePaths.length} paths selected
                                  </Text>
                                </Box>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>

                      {selectedPaths.length > 0 && (
                        <Box>
                          <Text fontSize="xs" color="gray.600" mb={2}>
                            Selected paths:
                          </Text>
                          <HStack wrap="wrap" gap={2}>
                            {selectedPaths.slice(0, 5).map(path => (
                              <Badge key={path} colorScheme="purple" size="sm">
                                {path}
                              </Badge>
                            ))}
                            {selectedPaths.length > 5 && (
                              <Badge colorScheme="gray" size="sm">
                                +{selectedPaths.length - 5} more
                              </Badge>
                            )}
                          </HStack>
                        </Box>
                      )}
                    </>
                  )}
                </VStack>
              </Box>
            </Box>

            {/* Description */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiInfo} color="gray.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Additional Information
                </Text>
              </HStack>

              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Description
                  </Text>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleFieldChange("description", e.target.value)}
                    placeholder="e.g., CDN for static assets, Analytics service, Font provider..."
                    rows={3}
                    disabled={createMutation.isPending}
                  />
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Add a description to help identify the purpose of this preconnect
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              disabled={
                createMutation.isPending ||
                !formData.url ||
                !!urlError
              }
              width="full"
            >
              <HStack gap={2}>
                {createMutation.isPending ? (
                  <>
                    <Icon as={FiLoader} className="animate-spin" />
                    <Text>Creating Preconnect Entry...</Text>
                  </>
                ) : (
                  <>
                    <Icon as={FiPlus} />
                    <Text>Create Preconnect Entry</Text>
                  </>
                )}
              </HStack>
            </Button>
          </VStack>

          {/* Sidebar */}
          <VStack gap={6} align="stretch">
            {/* Preview */}
            <Box
              bg="white"
              shadow="sm"
              borderColor="gray.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiZap} color="orange.500" boxSize={5} />
                <Text fontWeight="semibold" color="gray.900">
                  Entry Preview
                </Text>
              </HStack>

              <VStack align="stretch" gap={3}>
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    URL
                  </Text>
                  <Text fontSize="sm" fontFamily="mono" color={formData.url ? "black" : "gray.400"}>
                    {formData.url || "Not configured"}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Type
                  </Text>
                  <Badge colorScheme={formData.type === "preconnect" ? "blue" : "purple"}>
                    {formData.type === "preconnect" ? "Preconnect" : "DNS Prefetch"}
                  </Badge>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Applied To
                  </Text>
                  <Text fontSize="sm" color="black">
                    {isGlobal 
                      ? "All pages"
                      : selectedPaths.length > 0 
                        ? `${selectedPaths.length} specific path(s)`
                        : "No paths selected"}
                  </Text>
                </Box>

                {formData.description && (
                  <Box>
                    <Text fontSize="xs" color="gray.600" mb={1}>
                      Description
                    </Text>
                    <Text fontSize="sm">
                      {formData.description}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Tips */}
            <Box
              bg="blue.50"
              shadow="sm"
              borderColor="blue.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="blue.600" boxSize={5} />
                <Text fontWeight="semibold" color="blue.900">
                  Preconnect Tips
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="blue.800">
                <Text>• Use for critical third-party origins</Text>
                <Text>• Limit to 2-3 preconnects per page</Text>
                <Text>• DNS prefetch is lighter than preconnect</Text>
                <Text>• Monitor performance impact</Text>
                <Text>• Remove unused preconnects</Text>
              </VStack>
            </Box>
          </VStack>
        </Grid>

        {/* Success Dialog */}
        {open && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
          >
            <Box
              bg="white"
              rounded="lg"
              shadow="xl"
              p={8}
              maxW="md"
              width="90%"
            >
              <VStack gap={4} align="center">
                <Box
                  bg="green.100"
                  rounded="full"
                  p={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} color="green.600" boxSize={8} />
                </Box>
                <Text fontSize="2xl" fontWeight="bold">
                  Preconnect Entry Created!
                </Text>
                <Text color="gray.600" textAlign="center">
                  Your preconnect entry has been created successfully.
                </Text>

                {createdEntry && (
                  <Box
                    bg="gray.50"
                    p={4}
                    rounded="md"
                    width="100%"
                    border="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Resource URL:
                    </Text>
                    <HStack gap={2}>
                      <Text
                        fontFamily="mono"
                        fontSize="sm"
                        flex={1}
                        wordBreak="break-all"
                      >
                        {createdEntry.url}
                      </Text>
                      <Button size="sm" onClick={copyToClipboard}>
                        <HStack gap={1}>
                          <Icon as={FiCopy} />
                          <Text>Copy</Text>
                        </HStack>
                      </Button>
                    </HStack>
                  </Box>
                )}

                <HStack gap={3} width="full" mt={6}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      onClose();
                      setFormData({
                        url: "",
                        type: "preconnect",
                        description: "",
                        paths: ""
                      });
                      setSelectedPaths([]);
                      setIsGlobal(true);
                      setCreatedEntry(null);
                    }}
                    flex={1}
                  >
                    Create Another
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleGoToWebsite}
                    flex={1}
                  >
                    View All Entries
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

// Add spinning animation
if (typeof document !== 'undefined' && !document.querySelector('#create-preconnect-styles')) {
  const style = document.createElement("style");
  style.id = 'create-preconnect-styles';
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .animate-spin {
      animation: spin 1s linear infinite;
    }
  `;
  document.head.appendChild(style);
}